/**
 * space/shop.js — every Stars sink in the personal space (GDD 9.2–9.4).
 *
 * Shelf expansions, shelf styles, tank tiers, decorations, floor/wall themes.
 * All of them go through `economy.spendStars`, all of them refuse politely when
 * you cannot afford them, and all of them end by re-applying the model so the
 * room reflects the save rather than trying to patch itself in place.
 *
 * Seasonal themes are listed but never purchasable — the GDD wants them visible
 * as something to come back for.
 *
 * @module space/shop
 */

import * as THREE from 'three';
import {
  SPACE, SHELF_THEMES, SHELF_EXPANSION_COSTS, ROOM_THEMES, SPACE_MUSIC, EVENTS,
} from '../constants.js';
import { bus } from '../core/events.js';
import { save, economy } from '../core/save.js';
import { menuHost } from './pointer.js';
import { nextTankTier } from './fish-tank.js';
import { decorSpec } from './decor.js';

/** @type {{ own: object|null, refresh: () => void }} */
const host = { own: null, refresh: () => {} };

/**
 * @param {{ own: object, refresh: () => void }} deps
 */
export function attach(deps) {
  host.own = deps.own;
  host.refresh = deps.refresh;
}

function toast(text, kind = 'good', duration = 2600) {
  bus.emit(EVENTS.UI_TOAST, { text, kind, duration });
}

/** @private Report the shortfall rather than failing silently. */
function afford(cost, reason) {
  if (economy.spendStars(cost, reason)) return true;
  toast(`Need ${cost} stars — you have ${economy.stars()}`, 'bad');
  return false;
}

/**
 * Does the player already have this theme? Free themes count as owned.
 * @param {string} themeId
 * @returns {boolean}
 */
export function owns(themeId) {
  const t = SHELF_THEMES.concat(ROOM_THEMES).find((x) => x.id === themeId);
  if (t && t.cost === 0) return true;
  return save.data.space.unlockedThemes.includes(themeId);
}

// ── Shelves ─────────────────────────────────────────────────────────────────

/**
 * Buy another six-slot shelf unit (GDD 9.2 "Expansion").
 * @returns {boolean}
 */
export function buyShelfExpansion() {
  const units = Math.ceil(save.data.space.shelfSlots / SPACE.shelfSlotsPerUnit);
  const cost = SHELF_EXPANSION_COSTS[units - 2];
  if (cost === undefined || units >= SPACE.shelfBays) {
    toast('Every shelf is already built', 'bad');
    return false;
  }
  if (!afford(cost, 'shelf-expansion')) return false;
  save.data.space.shelfSlots += SPACE.shelfSlotsPerUnit;
  host.refresh();
  toast(`New shelf — ${save.data.space.shelfSlots} slots`, 'prize');
  return true;
}

/**
 * @param {string} themeId
 * @returns {boolean}
 */
export function setShelfTheme(themeId) {
  const t = SHELF_THEMES.find((x) => x.id === themeId);
  if (!t) return false;
  if (!owns(t.id)) {
    if (!afford(t.cost, 'shelf-theme')) return false;
    save.data.space.unlockedThemes.push(t.id);
  }
  save.data.space.theme = t.id;
  host.refresh();
  toast(`${t.name} shelves`, 'good');
  return true;
}

/** Open the shelf-style picker beside the first shelf bay. */
export function openShelfThemes() {
  if (!host.own) return;
  const anchor = host.own.room.anchors.shelfBays[0];
  menuHost.open(
    {
      key: 'shelf-themes',
      parent: host.own.room.root,
      position: new THREE.Vector3(anchor.position.x - 1.15, 1.7, anchor.position.z),
      rotationY: -Math.PI / 2,
    },
    {
      title: 'SHELF STYLE',
      idPrefix: 'own-shelf-style',
      items: SHELF_THEMES.map((t) => ({
        id: t.id,
        label: t.name,
        sub: owns(t.id) ? (save.data.space.theme === t.id ? 'in use' : 'owned') : `${t.cost}*`,
        disabled: !owns(t.id) && economy.stars() < t.cost,
      })),
      onPick: (id) => { menuHost.close(); setShelfTheme(id); },
    },
  );
}

// ── Tank ────────────────────────────────────────────────────────────────────

/**
 * Move up a tank tier, which raises the display capacity and swaps the model
 * (GDD 9.3 "Tank upgrades").
 * @returns {boolean}
 */
export function upgradeTank() {
  const next = nextTankTier(save.data.space.tankCapacity);
  if (!next) {
    toast('That is the biggest tank there is', 'bad');
    return false;
  }
  if (!afford(next.cost, 'tank-upgrade')) return false;
  save.data.space.tankCapacity = next.capacity;
  host.refresh();
  toast(`${next.name} — room for ${next.capacity} fish`, 'prize');
  return true;
}

// ── Decorations ─────────────────────────────────────────────────────────────

/**
 * @param {string} anchorId @param {string} catalogId
 * @returns {boolean}
 */
export function placeDecor(anchorId, catalogId) {
  const spec = decorSpec(catalogId);
  if (!spec) return false;
  if (!afford(spec.cost, 'decor')) return false;
  save.data.space.decor = save.data.space.decor.filter((d) => d.anchor !== anchorId);
  save.data.space.decor.push({ anchor: anchorId, catalogId: spec.id, color: spec.color });
  host.refresh();
  toast(`${spec.name} placed`, 'prize');
  return true;
}

/**
 * Take a decoration down. No refund — Stars are earned slowly enough that
 * refunds would turn the catalogue into a preview tool.
 * @param {string} anchorId
 * @returns {boolean}
 */
export function removeDecor(anchorId) {
  const before = save.data.space.decor.length;
  save.data.space.decor = save.data.space.decor.filter((d) => d.anchor !== anchorId);
  if (save.data.space.decor.length === before) return false;
  host.refresh();
  return true;
}

// ── Room ────────────────────────────────────────────────────────────────────

/**
 * @param {string} themeId
 * @returns {boolean}
 */
export function setRoomTheme(themeId) {
  const t = ROOM_THEMES.find((x) => x.id === themeId);
  if (!t) return false;
  if (t.locked) {
    toast(`${t.name} unlocks during its season`, 'bad');
    return false;
  }
  if (!owns(t.id)) {
    if (!afford(t.cost, 'room-theme')) return false;
    save.data.space.unlockedThemes.push(t.id);
  }
  save.data.space.roomTheme = t.id;
  host.own?.room.applyTheme(t.id);
  host.refresh();
  toast(`${t.name} it is`, 'good');
  return true;
}

/**
 * Choose the background track. The audio agent owns playback; this only decides
 * which id gets announced.
 * @param {string} trackId
 * @returns {boolean}
 */
export function setMusic(trackId) {
  const track = SPACE_MUSIC.find((m) => m.id === trackId);
  if (!track) return false;
  save.data.space.music = track.id;
  save.markDirty();
  if (host.own?.decor.model) host.own.decor.model.music = track.id;
  bus.emit(EVENTS.AUDIO_PLAY, { name: 'space-music', track: track.id });
  toast(`Now playing: ${track.name}`, 'good');
  return true;
}
